import { useEffect } from "react";
import { useSnapshot } from "valtio";
import { engagementContractStore, resetEngagementContractStore } from "./engagementContractStore";
import { EngagementContractService } from "@services/engagements/contracts/engagementContractService";
import { SpendAccountService } from "@services/finance/spendAccounts/spendAccountService";
import { CreditService } from "@services/finance/credits/creditService";
import { DebitService } from "@services/finance/debits/debitService";

const service = new EngagementContractService();
const spendAccountService = new SpendAccountService();
const creditService = new CreditService();
const debitService = new DebitService();

export function useEngagementContract(engagementId: number | undefined) {
  const snap = useSnapshot(engagementContractStore);

  useEffect(() => {
    if (engagementId == null) return;
    resetEngagementContractStore();
    engagementContractStore.engagementId = engagementId;
    loadData(engagementId);

    return () => {
      resetEngagementContractStore();
    };
  }, [engagementId]);

  async function loadData(id: number) {
    engagementContractStore.isLoading = true;
    engagementContractStore.error = null;
    try {
      const [available, detail] = await Promise.all([
        service.getAvailable(id),
        service.get(id),
      ]);
      engagementContractStore.availableContracts = available;
      engagementContractStore.assignedDetail = detail;
      if (detail) {
        engagementContractStore.selectedContractId = detail.contract.id;
        await loadSpend(detail.contract.id);
      }
    } catch {
      engagementContractStore.error = "Failed to load contract data.";
    } finally {
      engagementContractStore.isLoading = false;
    }
  }

  // Read-only spend account + ledger for the assigned contract.
  async function loadSpend(contractId: number) {
    const [spendAccount, credits, debits, balance] = await Promise.all([
      spendAccountService.get(contractId),
      creditService.list(contractId),
      debitService.list(contractId),
      spendAccountService.getBalance(contractId),
    ]);
    engagementContractStore.spendAccount = spendAccount;
    engagementContractStore.credits = credits;
    engagementContractStore.debits = debits;
    engagementContractStore.balance = balance ? balance.balance : null;
  }

  function clearSpend() {
    engagementContractStore.spendAccount = null;
    engagementContractStore.credits = [];
    engagementContractStore.debits = [];
    engagementContractStore.balance = null;
  }

  async function assignContract(contractId: number) {
    if (engagementId == null) return;
    engagementContractStore.isSaving = true;
    engagementContractStore.error = null;
    try {
      const detail = await service.assign(engagementId, { contract_id: contractId });
      engagementContractStore.assignedDetail = detail;
      engagementContractStore.selectedContractId = detail.contract.id;
      await loadSpend(detail.contract.id);
    } catch {
      engagementContractStore.error = "Failed to assign contract.";
    } finally {
      engagementContractStore.isSaving = false;
    }
  }

  async function removeContract() {
    if (engagementId == null) return;
    engagementContractStore.isSaving = true;
    engagementContractStore.error = null;
    try {
      await service.remove(engagementId);
      engagementContractStore.assignedDetail = null;
      engagementContractStore.selectedContractId = null;
      clearSpend();
    } catch {
      engagementContractStore.error = "Failed to remove contract.";
    } finally {
      engagementContractStore.isSaving = false;
    }
  }

  return {
    availableContracts: snap.availableContracts,
    assignedDetail: snap.assignedDetail,
    selectedContractId: snap.selectedContractId,
    spendAccount: snap.spendAccount,
    credits: snap.credits,
    debits: snap.debits,
    balance: snap.balance,
    isLoading: snap.isLoading,
    isSaving: snap.isSaving,
    error: snap.error,
    assignContract,
    removeContract,
  };
}